import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import { Toaster, toast } from 'sonner';
import config from './config';
import { VersionReloadToast } from './VersionReloadToast';

const ONE_HOUR_SECONDS = 60 * 60;
const RELOAD_TOASTER_ID = 'version-check-reload-toaster';

export const VersionCheck = () => {
  const location = useLocation();
  const initialVersion = config.VITE_VERSION ?? '';
  const [version, setVersion] = useState(() => ({
    current: initialVersion,
    latest: initialVersion,
  }));

  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const reloadToastIdRef = useRef<string | number | null>(null);
  const isReloadingRef = useRef(false);
  const reloadFallbackTimeoutRef = useRef<number | null>(null);

  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const prevLocationKeyRef = useRef(location.key);
  const stopCountdown = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCountdownSeconds(null);
  }, []);

  const performReload = useCallback(() => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;
    setIsReloading(true);
    setDismissedVersion(null);
    if (reloadToastIdRef.current !== null) {
      toast.dismiss(reloadToastIdRef.current);
      reloadToastIdRef.current = null;
    }
    stopCountdown();
    if (reloadFallbackTimeoutRef.current !== null) {
      window.clearTimeout(reloadFallbackTimeoutRef.current);
    }
    // If reload is blocked/cancelled, recover from "reloading" state.
    reloadFallbackTimeoutRef.current = window.setTimeout(() => {
      isReloadingRef.current = false;
      setIsReloading(false);
      reloadFallbackTimeoutRef.current = null;
    }, 1500);
    window.location.reload();
  }, [stopCountdown]);

  const startCountdown = useCallback(() => {
    stopCountdown();
    setCountdownSeconds(ONE_HOUR_SECONDS);

    intervalRef.current = window.setInterval(() => {
      if (document.hidden) return;

      setCountdownSeconds((s) => {
        if (s === null) return null;
        if (s <= 1) {
          performReload();
          return null;
        }
        return s - 1;
      });
    }, 1000);
  }, [performReload, stopCountdown]);

  // Poll latest version
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;

    const doVersionCheck = () => {
      if (document.hidden) return;

      fetch('/', { method: 'HEAD' })
        .then(
          (x) => {
            setVersion((v) => ({
              current: v.current,
              latest: x.headers.get('X-Version') ?? v.latest,
            }));
          },
          () => {},
        )
        .then(() => {
          t = setTimeout(doVersionCheck, 5000);
        });
    };

    const onVisibilityChange = () => {
      clearTimeout(t);
      doVersionCheck();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    doVersionCheck();

    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopCountdown();
    };
  }, []);

  // If user navigates anywhere: force reload immediately
  useEffect(() => {
    const navigated = prevLocationKeyRef.current !== location.key;
    prevLocationKeyRef.current = location.key;
    const updateRequired = version.current !== version.latest;

    if (!navigated) return;
    if (!updateRequired) return;
    if (isReloadingRef.current) return;

    isReloadingRef.current = true;
    if (reloadFallbackTimeoutRef.current !== null) {
      window.clearTimeout(reloadFallbackTimeoutRef.current);
    }
    reloadFallbackTimeoutRef.current = window.setTimeout(() => {
      isReloadingRef.current = false;
      reloadFallbackTimeoutRef.current = null;
    }, 1500);
    window.location.reload();
  }, [location.key, version]);

  const onNo = useCallback(() => {
    setDismissedVersion(version.latest);
    startCountdown();
  }, [startCountdown, version.latest]);

  const onToastActionMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>, handler: () => void) => {
    if (event.button !== 0) return;
    event.preventDefault();
    handler();
  }, []);

  const onToastActionClick = useCallback((event: MouseEvent<HTMLButtonElement>, handler: () => void) => {
    // Keep click for keyboard activation; mouse/touch runs on mousedown.
    if (event.detail !== 0) return;
    handler();
  }, []);

  useEffect(() => {
    const updateRequired = version.current !== version.latest;
    const isLoginPage = location.pathname === '/login';
    const shouldShowReloadToast =
      !isLoginPage &&
      !isReloading &&
      updateRequired &&
      (countdownSeconds !== null || dismissedVersion !== version.latest);
    if (!shouldShowReloadToast) {
      if (reloadToastIdRef.current !== null) {
        toast.dismiss(reloadToastIdRef.current);
        reloadToastIdRef.current = null;
      }
      return;
    }

    const toastId = toast.custom(
      () => (
        <VersionReloadToast
          countdownSeconds={countdownSeconds}
          onNo={onNo}
          onReload={performReload}
          onActionMouseDown={onToastActionMouseDown}
          onActionClick={onToastActionClick}
        />
      ),
      {
        duration: Infinity,
        unstyled: true,
        id: reloadToastIdRef.current ?? 'version-check-reload-toast',
        toasterId: RELOAD_TOASTER_ID,
        style: {
          zIndex: 2147483647,
          pointerEvents: 'auto',
          background: 'transparent',
          border: 'none',
          boxShadow: 'none',
          padding: 0,
        },
      },
    );
    reloadToastIdRef.current = toastId;
  }, [
    isReloading,
    version,
    dismissedVersion,
    countdownSeconds,
    onNo,
    performReload,
    onToastActionClick,
    onToastActionMouseDown,
    location.pathname,
  ]);

  useEffect(
    () => () => {
      if (reloadFallbackTimeoutRef.current !== null) {
        window.clearTimeout(reloadFallbackTimeoutRef.current);
      }
      if (reloadToastIdRef.current !== null) {
        toast.dismiss(reloadToastIdRef.current);
      }
    },
    [],
  );

  return (
    <>
      {typeof document !== 'undefined' &&
        createPortal(
          <Toaster
            id={RELOAD_TOASTER_ID}
            position="bottom-left"
            richColors
            closeButton={false}
            toastOptions={{
              style: {
                zIndex: 2147483647,
                pointerEvents: 'auto',
              },
            }}
          />,
          document.body,
        )}

      <span className="fixed bottom-0 left-0 z-[1020] px-2 py-1 text-[8pt] text-slate-400">{version.current}</span>
    </>
  );
};
